import { useState } from "react";
import { Progress } from "@/components/ui/progress";
import { Trash2, RefreshCw, CheckCircle, AlertCircle, Loader2, CloudUpload, Download, X, XCircle, Ban, ChevronDown, ChevronRight, AlertTriangle } from "lucide-react";

// --- Status helpers ---

function statusIcon(status, isStalled) {
  if (isStalled) return <AlertTriangle className="h-4 w-4 text-[#c6a96f]" />;
  switch (status) {
    case "completed": return <CheckCircle className="h-4 w-4 text-[#5fa87a]" />;
    case "failed": return <AlertCircle className="h-4 w-4 text-[#c47272]" />;
    case "cancelled": return <Ban className="h-4 w-4 text-[#52525b]" />;
    case "downloading": return <Download className="h-4 w-4 text-[#c6a96f] gold-pulse" />;
    case "uploading": return <CloudUpload className="h-4 w-4 text-[#c6a96f] gold-pulse" />;
    default: return <Loader2 className="h-4 w-4 text-[#52525b] animate-spin" />;
  }
}

function statusLabel(status, isStalled) {
  if (isStalled) return "Stalled";
  switch (status) {
    case "completed": return "Delivered";
    case "failed": return "Failed";
    case "cancelled": return "Cancelled";
    case "downloading": return "Pulling from Vimeo";
    case "uploading": return "Pushing to Dropbox";
    case "pending": return "In Queue";
    default: return status;
  }
}

function statusColor(status, isStalled) {
  if (isStalled) return "text-[#c6a96f]";
  switch (status) {
    case "completed": return "text-[#5fa87a]";
    case "failed": return "text-[#c47272]";
    case "cancelled": return "text-[#52525b]";
    case "downloading": case "uploading": return "text-[#c6a96f]";
    default: return "text-[#71717a]";
  }
}

function isStalled(t) {
  if (!["downloading", "uploading"].includes(t.status)) return false;
  if (!t.updated_at) return false;
  const updated = new Date(t.updated_at).getTime();
  const now = Date.now();
  return (now - updated) > 2 * 60 * 1000; // 2 minutes
}

// --- Transfer item ---

function TransferItem({ t, onCancelOne, onRetryOne }) {
  const stalled = isStalled(t);
  return (
    <div
      data-testid={`transfer-item-${t.id}`}
      className={`ug-card p-4 ${t.status === "cancelled" ? "ug-cancelled" : ""}`}
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5">{statusIcon(t.status, stalled)}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h4 className="text-sm font-medium text-[#e4e4e7] truncate">{t.video_name || t.video_uri}</h4>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className={`text-[10px] font-mono tracking-wider ${statusColor(t.status, stalled)}`}>
                {statusLabel(t.status, stalled)}
              </span>
              {["pending", "downloading", "uploading"].includes(t.status) && (
                <button
                  data-testid={`cancel-btn-${t.id}`}
                  onClick={() => onCancelOne(t.id)}
                  className="text-[#52525b] hover:text-[#c47272] transition-colors p-0.5"
                  title="Cancel"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
              {t.status === "failed" && onRetryOne && (
                <button
                  data-testid={`retry-btn-${t.id}`}
                  onClick={() => onRetryOne(t.id)}
                  className="text-[#52525b] hover:text-[#c6a96f] transition-colors p-0.5"
                  title="Retry this transfer"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>

          {["downloading", "uploading", "pending"].includes(t.status) && (
            <div className="mt-3">
              <div className="h-1.5 w-full bg-[#21252b] rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ease-out ${stalled ? "bg-[#c6a96f]/50" : "bg-[#c6a96f]"}`}
                  style={{ width: `${t.progress || 0}%` }}
                />
              </div>
              <div className="flex justify-between mt-1.5">
                <span className="text-[10px] text-[#52525b] font-mono">{Math.round(t.progress || 0)}%</span>
                {t.file_size > 0 && (
                  <span className="text-[10px] text-[#52525b] font-mono">
                    {(t.file_size / (1024 * 1024)).toFixed(0)} MB
                  </span>
                )}
              </div>
              {stalled && (
                <div className="mt-2 flex items-center gap-3">
                  <p className="text-[10px] text-[#c6a96f] font-mono flex-1">// stalled — no progress for 2+ minutes</p>
                  {onRetryOne && (
                    <button
                      data-testid={`retry-stalled-${t.id}`}
                      onClick={() => onRetryOne(t.id)}
                      className="btn-ghost px-3 py-1 rounded-lg text-[10px] flex items-center gap-1.5 !border-[#c6a96f]/30 !text-[#c6a96f] hover:!bg-[rgba(198,169,111,0.08)]"
                    >
                      <RefreshCw className="h-3 w-3" /> Retry
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {t.status === "completed" && t.dropbox_path && (
            <p className="text-[10px] text-[#52525b] mt-1.5 font-mono truncate">{t.dropbox_path}</p>
          )}

          {t.status === "failed" && t.error && (
            <p className="text-[10px] text-[#c47272] mt-1.5 line-clamp-2">{t.error}</p>
          )}
        </div>
      </div>
    </div>
  );
}

// --- Collapsible section ---

function TransferSection({ label, commentLabel, count, items, onCancelOne, onRetryOne, defaultOpen = true, accentColor = "#71717a" }) {
  const [open, setOpen] = useState(defaultOpen);

  if (count === 0) return null;

  return (
    <div className="mb-6">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 w-full text-left mb-3 group"
        data-testid={`section-toggle-${commentLabel}`}
      >
        {open
          ? <ChevronDown className="h-3.5 w-3.5 text-[#52525b] group-hover:text-[#a1a1aa] transition-colors" />
          : <ChevronRight className="h-3.5 w-3.5 text-[#52525b] group-hover:text-[#a1a1aa] transition-colors" />
        }
        <span className="font-mono text-[10px] tracking-[0.3em] uppercase" style={{ color: `${accentColor}99` }}>
          // {commentLabel}
        </span>
        <span className="font-mono text-[10px] tracking-wider" style={{ color: accentColor }}>
          {count}
        </span>
      </button>
      {open && (
        <div className="space-y-2.5 pl-5">
          {items.map((t) => (
            <TransferItem key={t.id} t={t} onCancelOne={onCancelOne} onRetryOne={onRetryOne} />
          ))}
        </div>
      )}
    </div>
  );
}

// --- Main panel ---

export default function TransferPanel({ transfers, onClearHistory, onRefresh, onCancelOne, onCancelAll, onRetryFailed, onRetryOne, onClearFailed }) {
  const active = transfers.filter(t => ["downloading", "uploading"].includes(t.status));
  const failed = transfers.filter(t => t.status === "failed");
  const queued = transfers.filter(t => t.status === "pending");
  const completed = transfers.filter(t => t.status === "completed");
  const cancelled = transfers.filter(t => t.status === "cancelled");
  const totalActive = active.length + queued.length;

  return (
    <div data-testid="transfer-panel">
      {/* Header */}
      <div className="mb-8 mt-4">
        <div className="section-label mb-3">// transfer queue</div>
        <h1 data-testid="transfers-title" className="text-3xl md:text-4xl font-bold tracking-tight text-[#f4f4f5]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          Transfers
        </h1>
      </div>

      {/* Summary bar */}
      <div data-testid="transfer-summary" className="ug-card p-4 mb-8 flex items-center gap-6 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="h-2 w-2 rounded-full bg-[#c6a96f]" />
          <span className="text-xs font-mono text-[#a1a1aa]">
            {active.length} active
          </span>
        </div>
        {failed.length > 0 && (
          <div className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-[#c47272]" />
            <span className="text-xs font-mono text-[#c47272]">
              {failed.length} failed
            </span>
          </div>
        )}
        <div className="flex items-center gap-2">
          <div className="h-2 w-2 rounded-full bg-[#52525b]" />
          <span className="text-xs font-mono text-[#a1a1aa]">
            {queued.length} queued
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="h-2 w-2 rounded-full bg-[#5fa87a]" />
          <span className="text-xs font-mono text-[#a1a1aa]">
            {completed.length} delivered
          </span>
        </div>
        {cancelled.length > 0 && (
          <div className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-[#3f3f46]" />
            <span className="text-xs font-mono text-[#52525b]">
              {cancelled.length} cancelled
            </span>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3 mb-8 flex-wrap">
        <button data-testid="refresh-transfers-btn" onClick={onRefresh} className="btn-ghost px-3 py-2 rounded-xl text-xs flex items-center gap-2">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
        {failed.length > 0 && (
          <button data-testid="retry-failed-btn" onClick={onRetryFailed} className="btn-gold px-4 py-2 rounded-xl text-xs flex items-center gap-2">
            <RefreshCw className="h-3.5 w-3.5" /> Retry {failed.length} Failed
          </button>
        )}
        {failed.length > 0 && (
          <button data-testid="clear-failed-btn" onClick={onClearFailed} className="btn-ghost px-3 py-2 rounded-xl text-xs flex items-center gap-2 !text-[#c47272] !border-[#c47272]/30 hover:!bg-[#c47272]/5">
            <Trash2 className="h-3.5 w-3.5" /> Clear Failed
          </button>
        )}
        {totalActive > 0 && (
          <button data-testid="cancel-all-btn" onClick={onCancelAll} className="btn-ghost px-3 py-2 rounded-xl text-xs flex items-center gap-2 !text-[#c47272] !border-[#c47272]/30 hover:!bg-[#c47272]/5">
            <XCircle className="h-3.5 w-3.5" /> Cancel All ({totalActive})
          </button>
        )}
        <button data-testid="clear-history-btn" onClick={onClearHistory} disabled={transfers.length === 0} className="btn-ghost px-3 py-2 rounded-xl text-xs flex items-center gap-2 disabled:opacity-30">
          <Trash2 className="h-3.5 w-3.5" /> Clear History
        </button>
      </div>

      {/* Grouped transfer list */}
      {transfers.length === 0 ? (
        <div data-testid="no-transfers" className="flex flex-col items-center justify-center py-24 gap-4">
          <CloudUpload className="h-10 w-10 text-[#3f3f46]" />
          <p className="text-sm text-[#71717a]">No transfers yet</p>
          <p className="text-xs text-[#52525b] font-mono">// select some footage and hit transfer</p>
        </div>
      ) : (
        <>
          <TransferSection
            label="Active"
            commentLabel="active"
            count={active.length}
            items={active}
            onCancelOne={onCancelOne}
            onRetryOne={onRetryOne}
            defaultOpen={true}
            accentColor="#c6a96f"
          />
          <TransferSection
            label="Failed"
            commentLabel="needs attention"
            count={failed.length}
            items={failed}
            onCancelOne={onCancelOne}
            onRetryOne={onRetryOne}
            defaultOpen={true}
            accentColor="#c47272"
          />
          <TransferSection
            label="Queued"
            commentLabel="in queue"
            count={queued.length}
            items={queued}
            onCancelOne={onCancelOne}
            defaultOpen={true}
            accentColor="#a1a1aa"
          />
          <TransferSection
            label="Completed"
            commentLabel="delivered"
            count={completed.length}
            items={completed}
            onCancelOne={onCancelOne}
            defaultOpen={false}
            accentColor="#5fa87a"
          />
          <TransferSection
            label="Cancelled"
            commentLabel="cancelled"
            count={cancelled.length}
            items={cancelled}
            onCancelOne={onCancelOne}
            defaultOpen={false}
            accentColor="#52525b"
          />
        </>
      )}
    </div>
  );
}
